import GridRowModel from '../../Grid/data/GridRowModel.js';
import PartOfProject from '../data/mixin/PartOfProject.js';
import ResourceModelMixin from './mixin/ResourceModelMixin.js';
/* <remove-on-release> */
import { BaseResourceMixin } from '../../Engine/quark/model/scheduler_basic/BaseResourceMixin.js';
/* </remove-on-release> */
import { CoreResourceMixin } from '../../Engine/quark/model/scheduler_core/CoreResourceMixin.js';

const EngineMixin = /* <remove-on-release> */globalThis.bryntum?.useBasicEngine ? BaseResourceMixin : /* </remove-on-release> */CoreResourceMixin;

/**
 * @module Scheduler/model/ResourceModel
 */

/**
 * This class represent a single Resource in scheduler, usually added to a {@link Scheduler.data.ResourceStore}.
 *
 * It is a subclass of  {@link Core.data.Model}. Please refer to the documentation for that class to become familiar
 * with the base interface of the resource.
 *
 * ## Fields and references
 *
 * A resource has a few predefined fields, see Fields below. If you want to add more fields with meta data describing
 * your resources then you should subclass this class:
 *
 * ```javascript
 * class MyResource extends ResourceModel {
 *   static fields = [
 *       // "id" and "name" fields are already provided by the superclass
 *       { name: 'company', type : 'string' }
 *   ];
 * }
 * ```
 *
 * If you want to use other names in your data for the id and name fields you can configure them as seen below:
 *
 * ```javascript
 * class MyResource extends ResourceModel {
 *   static fields = [
 *      { name: 'name', dataSource: 'userName' }
 *   ];
 * }
 * ```
 *
 * After load and project normalization, these references are accessible (assuming their respective stores are loaded):
 * - `{@link #property-assignments}` - The linked assignment records
 * - `{@link #property-events}` - The linked (through assignments) event records
 *
 * ## Async resolving of references
 *
 * As described above, a resource has links to assignments and events. These references are populated async, using the
 * calculation engine of the project that the resource via its store is a part of. Because of this asyncness, references
 * cannot be used immediately after assignment modifications:
 *
 * ```javascript
 * assignment.resourceId = 2;
 * // resource.assignments is not yet up to date
 * ```
 *
 * To make sure references are updated, wait for calculations to finish:
 *
 * ```javascript
 * assignment.resourceId = 2;
 * await assignment.project.commitAsync();
 * // resource.assignments is up to date
 * ```
 *
 * As an alternative, you can also use `setAsync()` to trigger calculations directly after the change:
 *
 * ```javascript
 * await assignment.setAsync({ resourceId : 2});
 * // resource.assignments is up to date
 * ```
 *
 * @extends Grid/data/GridRowModel
 * @mixes Scheduler/model/mixin/ResourceModelMixin
 */
export default class ResourceModel extends ResourceModelMixin(PartOfProject(EngineMixin.derive(GridRowModel))) {
    /**
     * Get associated events
     *
     * @member {Scheduler.model.EventModel[]} events
     * @readonly
     * @category Common
     */
    /**
     * Get associated {@link Scheduler/model/ResourceTimeRangeModel resource time ranges}. Only available when using
     * the `ResourceTimeRanges` feature.
     *
     * @member {Scheduler.model.ResourceTimeRangeModel[]} timeRanges
     * @readonly
     * @category Common
     */
    static $name = 'ResourceModel';

}

ResourceModel.exposeProperties();
